import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export type OperatorDashboardTourStatus = 'completed' | 'suppressed';

/**
 * Remembers whether this operator user has finished or dismissed the Dashboard guide.
 * The welcome dialog is offered only for the first login after the account is created.
 * Replay from Help starts the guide at step 1.
 */
@Injectable({ providedIn: 'root' })
export class OperatorDashboardTourService {
	static readonly firstLoginSessionKey = 'xpress.operator.dashboardTour.firstLogin';

	private readonly storagePrefix = 'xpress.operator.dashboardTour.';
	private readonly replaySubject = new Subject<void>();
	private pendingReplay = false;

	readonly replay$ = this.replaySubject.asObservable();

	shouldOffer(userId: string): boolean {
		const status = this.read(userId);
		return status !== 'completed' && status !== 'suppressed';
	}

	suppress(userId: string): void {
		this.write(userId, 'suppressed');
	}

	complete(userId: string): void {
		this.write(userId, 'completed');
	}

	requestReplay(_userId: string): void {
		this.pendingReplay = true;
		this.replaySubject.next();
	}

	consumeReplay(): boolean {
		if (!this.pendingReplay) {
			return false;
		}
		this.pendingReplay = false;
		return true;
	}

	/** The account's first authentication. Later logins clear this. */
	static markFirstLogin(): void {
		try {
			localStorage.setItem(OperatorDashboardTourService.firstLoginSessionKey, '1');
		} catch {
			// The guide can still be opened from Help when storage is blocked.
		}
	}

	static clearFirstLogin(): void {
		try {
			localStorage.removeItem(OperatorDashboardTourService.firstLoginSessionKey);
		} catch {
			// Logout still completes when storage cannot be cleared.
		}
	}

	static isFirstLogin(): boolean {
		try {
			return localStorage.getItem(OperatorDashboardTourService.firstLoginSessionKey) === '1';
		} catch {
			return false;
		}
	}

	private key(userId: string): string {
		return `${this.storagePrefix}${userId || '0'}`;
	}

	private read(userId: string): string | null {
		try {
			return localStorage.getItem(this.key(userId));
		} catch {
			return null;
		}
	}

	private write(userId: string, status: OperatorDashboardTourStatus): void {
		try {
			localStorage.setItem(this.key(userId), status);
		} catch {
			// Private mode and blocked storage still let the guide run for this visit.
		}
	}
}
